// Signing in with somebody else's identity, and only signing in.
//
// This proves which account the person owns. It cannot produce key material
// and must never look as though it does: a Google ID token is a signed
// assertion the issuer can mint without the user's device, so an archive
// unlocked by one would be an archive Google could read (§13).
//
// A Google account therefore always needs a second, key-bearing credential —
// a passkey — before it can hold anything.
//
// The Firebase SDK is imported dynamically. Statically it costs 200 kB of the
// main bundle to render one button, and someone signing in with a passkey — the
// path this product actually prefers — never needs a line of it.

export interface FirebaseWebConfig {
  apiKey: string; authDomain: string; projectId: string; appId: string;
}

type Auth = import('firebase/auth').Auth;

let auth: Auth | null = null;

/**
 * O domínio de autenticação tem de ser a própria origem da página.
 *
 * Desde o Safari 16.1 o armazenamento do `authDomain` é de terceiros quando
 * ele não coincide com a página, e o retorno do redirecionamento chega sem a
 * sessão que o iniciou — o login dá a volta e volta deslogado.
 *
 * O servidor não pode decidir isto: a Firebase Hosting reescreve para o Cloud
 * Run trocando o Host (medido — a API vê `...run.app`), então `/v1/auth/config`
 * responde o mesmo valor para todas as origens. Quem sabe onde está é o
 * navegador, e qualquer site de Hosting deste projeto serve
 * `/__/auth/handler` — okazip.com e app.okazip.com inclusive.
 *
 * Fica de fora só o que não é servido pela Hosting: localhost e endereços IP
 * em desenvolvimento, que continuam com o valor configurado.
 */
function sameOrigin(config: FirebaseWebConfig): FirebaseWebConfig {
  if (typeof location === 'undefined') return config;
  const host = location.hostname;
  const served = host.includes('.')
    && !/^\d+\.\d+\.\d+\.\d+$/.test(host)
    && !host.endsWith('.localhost');
  return served && host !== config.authDomain ? { ...config, authDomain: host } : config;
}

async function configure(config: FirebaseWebConfig): Promise<Auth> {
  if (auth) return auth;
  const [{ initializeApp, getApps }, { getAuth }] = await Promise.all([
    import('firebase/app'),
    import('firebase/auth'),
  ]);
  auth = getAuth(getApps()[0] ?? initializeApp(sameOrigin(config)));
  return auth;
}

/**
 * An app launched from the home screen, with no browser around it.
 *
 * `navigator.standalone` is the iPhone's own answer and the only one it gives;
 * the display-mode query is what everybody else answers.
 */
/**
 * Dentro do app iOS.
 *
 * A casca anexa isto ao User-Agent porque nada mais a denuncia, e duas
 * coisas dependem de saber: o login tem de ser por redirecionamento, e o
 * Google não pode ser oferecido ali — a política dele recusa OAuth em
 * webview embutida, e a passkey que o caminho do Google pede não funciona
 * num WKWebView sem Associated Domains.
 */
export const inNativeShell = () =>
  typeof navigator !== 'undefined' && /OkaZipApp\//.test(navigator.userAgent);

function standalone(): boolean {
  if (inNativeShell()) return true;
  if ((navigator as { standalone?: boolean }).standalone) return true;
  return typeof matchMedia === 'function'
    && matchMedia('(display-mode: standalone)').matches;
}

/**
 * What the page went to Google *for*, kept while it is away.
 *
 * Signing in and linking an account both end in an ID token and mean
 * opposite things: one says who you are, the other adds a way into the
 * archive you are already in. Coming back without knowing which would sign
 * somebody into a different archive than the one they were looking at.
 */
export type Intent = 'signin' | 'link';
const AWAY = 'wa.google.redirect';
export const returningFromIdp = (): Intent | null => {
  try {
    const v = sessionStorage.getItem(AWAY);
    return v === 'signin' || v === 'link' ? v : null;
  } catch { return null; }
};

/** Who is being asked. Firebase takes both through the same door. */
export type Provider = 'google' | 'apple';

/**
 * Returns an Identity Platform ID token for the API to verify, or null when
 * the page is on its way to the provider and will come back through
 * `tokenAfterRedirect`.
 *
 * A popup everywhere it works, because a redirect reloads the page. It does
 * not work in an app launched from the home screen: there is no browser to
 * put a second window in, so iOS either opens Safari as a separate app —
 * losing the opener the callback needs — or does nothing at all. Which is
 * what "the Google tab does not open" was: a promise that never settled.
 */
export async function signIn(
  config: FirebaseWebConfig, which: Provider = 'google', intent: Intent = 'signin',
  /** Nunca um popup. Verdadeiro quando esta janela está no lugar do app
   *  iOS: ela é uma folha de autenticação, e um popup ali é uma segunda
   *  janela sobre a primeira — "uma página abre a outra". */
  forceRedirect = false,
): Promise<string | null> {
  const [a, { GoogleAuthProvider, OAuthProvider, signInWithPopup, signInWithRedirect }] =
    await Promise.all([configure(config), import('firebase/auth')]);

  let provider;
  if (which === 'apple') {
    // Apple gives a name only on the very first authorisation and never
    // again, so asking for it here is the one chance — and an account with
    // no name has no way to tell which messages are yours (see ownerOf).
    const apple = new OAuthProvider('apple.com');
    apple.addScope('email');
    apple.addScope('name');
    provider = apple;
  } else {
    const google = new GoogleAuthProvider();
    google.setCustomParameters({ prompt: 'select_account' });
    provider = google;
  }

  if (forceRedirect || standalone()) {
    try { sessionStorage.setItem(AWAY, intent); } catch { /* private mode */ }
    await signInWithRedirect(a, provider);
    return null;                       // the page is leaving
  }
  const result = await signInWithPopup(a, provider);
  return result.user.getIdToken();
}

/**
 * Uma sessão a partir de um token que a Google já emitiu.
 *
 * É assim que as apps nativas entram, e é muito menos do que o caminho
 * web: sem `/__/auth/handler`, sem iframe, sem redirecionamento e sem
 * `getRedirectResult`. A casca do iOS fala com a Google pelo mecanismo do
 * próprio sistema e passa aqui o `id_token`; o Firebase troca-o por uma
 * sessão numa chamada só.
 *
 * O token é emitido para o cliente iOS deste projeto, e o Firebase aceita
 * qualquer cliente OAuth do mesmo projeto — é o que faz o SDK da Google.
 */
export async function signInWithGoogleIdToken(
  config: FirebaseWebConfig, idToken: string,
): Promise<string> {
  const [a, { GoogleAuthProvider, signInWithCredential }] = await Promise.all([
    configure(config),
    import('firebase/auth'),
  ]);
  const cred = GoogleAuthProvider.credential(idToken);
  const result = await signInWithCredential(a, cred);
  return result.user.getIdToken();
}

/**
 * Uma sessão a partir de um token que a Apple já emitiu.
 *
 * O `rawNonce` é obrigatório: a Apple assina no token o SHA-256 do que lhe
 * demos, e o Firebase compara-o com o original para saber que este token
 * foi pedido agora e por nós, em vez de apanhado noutro sítio.
 */
export async function signInWithAppleIdToken(
  config: FirebaseWebConfig, idToken: string, rawNonce: string,
): Promise<string> {
  const [a, { OAuthProvider, signInWithCredential }] = await Promise.all([
    configure(config),
    import('firebase/auth'),
  ]);
  const cred = new OAuthProvider('apple.com').credential({ idToken, rawNonce });
  const result = await signInWithCredential(a, cred);
  return result.user.getIdToken();
}

/**
 * The token from a sign-in that went via Google's own page.
 *
 * Called only when `returningFromGoogle` says so: `getRedirectResult` needs
 * the Firebase SDK, and loading 200 kB on every page load to ask a question
 * whose answer is almost always no is the thing the dynamic import exists to
 * avoid.
 */
export async function tokenAfterRedirect(config: FirebaseWebConfig): Promise<string | null> {
  try { sessionStorage.removeItem(AWAY); } catch { /* private mode */ }
  const [a, { getRedirectResult }] = await Promise.all([
    configure(config),
    import('firebase/auth'),
  ]);
  const result = await getRedirectResult(a);
  if (result) return result.user.getIdToken();
  // `getRedirectResult` só entrega uma vez. Se alguém já a consumiu — uma
  // segunda chamada, um efeito que voltou a correr — ela devolve `null`
  // com a pessoa autenticada à mesma, e tratar isso como "não entrou"
  // deita fora um login que aconteceu.
  return a.currentUser ? a.currentUser.getIdToken() : null;
}

/** Se há utilizador do Firebase, independentemente de quem consumiu o quê. */
export async function whoIsSignedIn(config: FirebaseWebConfig): Promise<string | null> {
  const a = await configure(config);
  return a.currentUser ? a.currentUser.uid.slice(0, 6) : null;
}

/**
 * Firebase's error codes are not sentences. `null` means the person cancelled —
 * closing a popup is a decision, not a failure, and showing them an error for
 * it is just noise.
 */
export function explain(e: unknown): string | null {
  const code = (e as { code?: string })?.code ?? '';
  switch (code) {
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
    case 'auth/user-cancelled':
      return null;
    case 'auth/popup-blocked':
      return 'Your browser blocked the Google window. Allow pop-ups for this site, then try again.';
    case 'auth/unauthorized-domain':
      return 'Sign-in is not authorised for this address.';
    case 'auth/operation-not-allowed':
      // O provedor existe no código e não no console do Firebase. Só deve
      // aparecer durante a configuração — a bandeira appleAvailable é o que
      // mantém o botão escondido até lá.
      return 'That way in is not switched on for this deployment yet.';
    case 'auth/network-request-failed':
      return 'Could not reach Google. Check your connection and try again.';
    case 'auth/account-exists-with-different-credential':
      return 'That email already signs in another way. Use the method you set up first.';
    default:
      return code ? `Google sign-in failed (${code}).` : String((e as Error)?.message ?? e);
  }
}

/** Signs out of the identity provider. The archive key was never there. */
export async function signOut() {
  if (!auth) return;
  const { signOut: leave } = await import('firebase/auth');
  await leave(auth);
}
